import { ChevronRight } from 'lucide-react'
import { Button, Label, Checkbox, RadioGroup, RadioGroupItem } from '@/components/ui'
import type { ModifierOption } from '@/types/modifier'
import type { MenuItem } from '@/types/menu'
import type { SelectedModifier } from '@/types/cart'
import { rowHasDynamicPricing, resolveIngredientPrice } from '@/utils/ingredientPricing'
import { formatPrice } from '@/utils/formatting'
import { QuantityRow } from './QuantityRow'

interface ModifierGroupProps {
  groupId: string
  groupName: string
  selectionType?: 'single' | 'multiple' | 'quantity'
  options: ModifierOption[]
  selectedOptions: SelectedModifier[]
  maxSelections: number
  minSelections?: number
  maxQuantityPerItem?: number | null
  onToggle: (
    groupId: string,
    groupName: string,
    optionId: string,
    optionName: string,
    price: number,
    variationId?: string,
    variationName?: string
  ) => void
  onQuantityChange?: (groupId: string, groupName: string, optionId: string, optionName: string, price: number, newQty: number) => void
  isIngredientEnabled: (optionId: string) => boolean
  onOpenVariationPicker?: (option: ModifierOption) => void
  menuItem?: MenuItem
}

export function ModifierGroup({
  groupId,
  groupName,
  selectionType,
  options,
  selectedOptions,
  maxSelections,
  minSelections = 0,
  maxQuantityPerItem,
  onToggle,
  onQuantityChange,
  isIngredientEnabled,
  onOpenVariationPicker,
  menuItem,
}: ModifierGroupProps) {
  const selectedCount = selectedOptions.length
  const maxReached = selectedCount >= maxSelections
  const isSingleSelection = maxSelections === 1
  const selectedValue = selectedOptions[0]?.optionId || ''

  const handleRadioChange = (optionId: string) => {
    const option = options.find((o) => o.id === optionId)
    if (option && isIngredientEnabled(optionId)) {
      // If option has variations, always open picker
      if (option.variationGroup && onOpenVariationPicker) {
        onOpenVariationPicker(option)
      } else {
        onToggle(groupId, groupName, optionId, option.name, option.priceAdjustment || 0)
      }
    }
  }

  // Dynamic pricing helpers
  const isDynamic = menuItem ? rowHasDynamicPricing(menuItem, groupId) : false
  const freeQuantity = menuItem?.rowPricingRules?.[groupId]?.freeQuantity ?? 0
  const pricingLabel = menuItem?.rowPricingRules?.[groupId]?.label

  // Compute display price for an option based on current selection state
  const getDisplayPrice = (optionId: string): { label: string; value: number } | null => {
    if (!menuItem || !isDynamic) return null

    const overridePrice = menuItem.customizationPrices?.[optionId]
    if (overridePrice !== undefined) {
      return { label: `+${formatPrice(overridePrice, menuItem?.currency, false)}`, value: overridePrice }
    }

    const nonOverrideSelected = selectedOptions.filter(
      (m) => !menuItem.customizationPrices?.[m.optionId]
    )
    const totalNonOverrideQty = nonOverrideSelected.reduce((sum, m) => sum + (m.quantity ?? 1), 0)
    const isSelected = nonOverrideSelected.some((m) => m.optionId === optionId)
    const currentIndex = isSelected
      ? nonOverrideSelected
          .filter((m) => m.optionId !== optionId)
          .reduce((sum, m) => sum + (m.quantity ?? 1), 0)
      : totalNonOverrideQty

    const price = resolveIngredientPrice(menuItem, groupId, optionId, currentIndex)
    if (price === 0) return { label: 'Included', value: 0 }
    return { label: `+${formatPrice(price, menuItem?.currency, false)}`, value: price }
  }

  // Quantity mode: render stepper UI
  if (selectionType === 'quantity' && onQuantityChange) {
    const qtyMap: Record<string, number> = {}
    for (const mod of selectedOptions) {
      qtyMap[mod.optionId] = mod.quantity ?? 1
    }

    const qtyIngredients = options.map((opt) => {
      const dynamicPrice = getDisplayPrice(opt.id)
      return {
        id: opt.id,
        name: opt.name,
        price: dynamicPrice ? dynamicPrice.value : opt.priceAdjustment,
        isEnabled: isIngredientEnabled(opt.id),
      }
    })

    const handleQtyChange = (optionId: string, newQty: number) => {
      const opt = options.find((o) => o.id === optionId)
      if (!opt) return
      const effectivePrice = isDynamic ? 0 : opt.priceAdjustment
      onQuantityChange(groupId, groupName, optionId, opt.name, effectivePrice, newQty)
    }

    return (
      <div className="space-y-3">
        <QuantityRow
          rowTitle={groupName}
          minSelections={minSelections}
          maxSelections={maxSelections}
          maxQuantityPerItem={maxQuantityPerItem ?? null}
          ingredients={qtyIngredients}
          quantities={qtyMap}
          onQuantityChange={handleQtyChange}
          freeQuantity={freeQuantity}
          isDynamic={isDynamic}
          pricingLabel={pricingLabel}
        />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-base font-semibold">
          {groupName}
          {isSingleSelection && (
            <>
              <span className="text-destructive ml-1" aria-hidden="true">*</span>
              <span className="sr-only">(required)</span>
            </>
          )}
        </Label>
        {!isSingleSelection && (
          <span className="text-sm text-muted-foreground">
            ({selectedCount}/{maxSelections})
          </span>
        )}
        {pricingLabel && (
          <span className="text-xs text-muted-foreground">
            · {pricingLabel}
          </span>
        )}
      </div>

      {isSingleSelection ? (
        <RadioGroup value={selectedValue} onValueChange={handleRadioChange} className="space-y-2" required>
          {options.map((option) => {
            const isDisabledByRule = !isIngredientEnabled(option.id)
            const isSelected = selectedValue === option.id

            return (
              <div
                key={option.id}
                className={`
                  flex items-center space-x-3 border rounded-lg p-3 transition-colors
                  ${isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}
                  ${isDisabledByRule ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <RadioGroupItem
                  value={option.id}
                  id={option.id}
                  disabled={isDisabledByRule}
                />
                <Label
                  htmlFor={option.id}
                  className="flex-1 cursor-pointer flex items-center justify-between gap-2"
                >
                  <div className="flex-1 min-w-0">
                    <span>{option.name}</span>
                    {isSelected && selectedOptions[0]?.variationName && (
                      <span className="text-xs text-muted-foreground ml-1">
                        ({selectedOptions[0].variationName})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {option.priceAdjustment !== 0 && (
                      <span className="text-sm font-semibold">
                        {option.priceAdjustment > 0 ? '+' : ''}{formatPrice(Math.abs(option.priceAdjustment), menuItem?.currency, false)}
                      </span>
                    )}
                    {option.variationGroup && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (onOpenVariationPicker) {
                            onOpenVariationPicker(option)
                          }
                        }}
                      >
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                </Label>
              </div>
            )
          })}
        </RadioGroup>
      ) : (
        <div className="space-y-2">
          {options.map((option) => {
            const isSelected = selectedOptions.some(
              (m) => m.groupId === groupId && m.optionId === option.id
            )
            const isDisabledByRule = !isIngredientEnabled(option.id)
            const isDisabled = isDisabledByRule || (!isSelected && maxReached)

            return (
              <div
                key={option.id}
                onClick={() => {
                  if (isDisabled) return
                  // If option has variations, always open picker
                  if (option.variationGroup && onOpenVariationPicker) {
                    onOpenVariationPicker(option)
                  } else {
                    onToggle(groupId, groupName, option.id, option.name, option.priceAdjustment || 0)
                  }
                }}
                className={`
                  flex items-center space-x-3 border rounded-lg p-3 cursor-pointer transition-colors
                  ${isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}
                  ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <Checkbox
                  id={option.id}
                  checked={isSelected}
                  disabled={isDisabled}
                  onCheckedChange={() => {
                    if (isDisabled) return
                    // If option has variations, always open picker
                    if (option.variationGroup && onOpenVariationPicker) {
                      onOpenVariationPicker(option)
                    } else {
                      onToggle(groupId, groupName, option.id, option.name, option.priceAdjustment || 0)
                    }
                  }}
                />
                <Label
                  htmlFor={option.id}
                  className="flex-1 cursor-pointer flex items-center justify-between gap-2"
                >
                  <div className="flex-1 min-w-0">
                    <span>{option.name}</span>
                    {isSelected && (
                      <>
                        {(() => {
                          const selectedMod = selectedOptions.find((s) => s.optionId === option.id)
                          return selectedMod?.variationName ? (
                            <span className="text-xs text-muted-foreground ml-1">
                              ({selectedMod.variationName})
                            </span>
                          ) : null
                        })()}
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {option.priceAdjustment !== 0 && (
                      <span className="text-sm font-semibold">
                        {option.priceAdjustment > 0 ? '+' : ''}{formatPrice(Math.abs(option.priceAdjustment), menuItem?.currency, false)}
                      </span>
                    )}
                    {option.variationGroup && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (onOpenVariationPicker) {
                            onOpenVariationPicker(option)
                          }
                        }}
                      >
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                </Label>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
